// @sentry/react-native → @sentry/react adapter.
//
// src/services/sentry/sentry.ts stays untouched: it calls init / wrap /
// captureException / captureMessage / setUser with React Native options.
// This maps those onto the browser SDK, drops the native-only options, and
// keeps the app's beforeSend / beforeBreadcrumb scrubbers in force.

import * as Sentry from '@sentry/react';
import React from 'react';

type InitOptions = Record<string, unknown> & {
  dsn?: string;
  environment?: string;
  tracesSampleRate?: number;
  sendDefaultPii?: boolean;
  beforeSend?: (event: unknown) => unknown;
  beforeBreadcrumb?: (breadcrumb: unknown) => unknown;
};

// Native-only options the RN SDK accepts that the browser SDK does not.
const NATIVE_ONLY = new Set([
  'enableAutoSessionTracking',
  'attachScreenshot',
  'attachViewHierarchy',
  'enableNative',
  'enableNativeCrashHandling',
  'enableNdk',
  'enableAppHangTracking',
]);

export function init(options: InitOptions = {}): void {
  const browserOptions: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(options)) {
    if (!NATIVE_ONLY.has(k)) browserOptions[k] = v;
  }
  Sentry.init({
    ...browserOptions,
    // Web release identity (finalkey-web@<commit>, stamped by the build);
    // the mobile side tags by app version. Preview deploys report as
    // 'preview' so they never pollute production's issue list.
    release: browserOptions.release ?? process.env.FK_WEB_RELEASE ?? 'finalkey-web@dev',
    environment: process.env.FK_WEB_ENV ?? browserOptions.environment,
    // Sessions are the phone's concern; keep the web surface quiet.
    autoSessionTracking: false,
    // No performance tracing until the beta has a telemetry budget.
    integrations: (defaults: unknown[]) => defaults,
  } as Parameters<typeof Sentry.init>[0]);
}

/** Mirrors `Sentry.wrap(App)` on native: render errors become events. */
export function wrap<T>(component: T): T {
  const Wrapped = Sentry.withErrorBoundary(component as unknown as React.ComponentType, {
    fallback: React.createElement('div', null),
  });
  return Wrapped as unknown as T;
}

export const captureException = Sentry.captureException;
export const captureMessage = Sentry.captureMessage;
export const setUser = Sentry.setUser;
export const setTag = Sentry.setTag;
export const setContext = Sentry.setContext;
export const addBreadcrumb = Sentry.addBreadcrumb;
export const withScope = Sentry.withScope;

export default {
  init,
  wrap,
  captureException,
  captureMessage,
  setUser,
  setTag,
  setContext,
  addBreadcrumb,
  withScope,
};
